<template>
  <UiContainer class="favorites-page">
    <div class="favorites-page__header">
      <h1 class="favorites-page__title">Избранное</h1>
      <p class="favorites-page__subtitle">Ваши избранные товары</p>
    </div>

    <div v-if="favorites.length === 0" class="favorites-page__empty">
      <span class="favorites-page__empty-icon" aria-hidden="true">♡</span>
      <h2 class="favorites-page__empty-title">Избранное пусто</h2>
      <p class="favorites-page__empty-text">
        Добавьте товары в избранное, чтобы они отображались здесь
      </p>
      <UiButton to="/catalog">Перейти в каталог</UiButton>
    </div>

    <div v-else>
      <div class="favorites-page__toolbar">
        <div>
          <h2 class="favorites-page__count">Избранные товары ({{ favorites.length }})</h2>
          <p class="favorites-page__count-sub">
            {{ favorites.length }} {{ pluralize(favorites.length, 'товар', 'товара', 'товаров') }}
          </p>
        </div>
        <div class="favorites-page__actions">
          <UiButton variant="secondary" :disabled="favorites.length === 0" @click="clearFavorites">
            Очистить избранное
          </UiButton>
          <UiButton v-if="!isSupplier" :disabled="favorites.length === 0" @click="createOrder">
            Оформить заказ
          </UiButton>
        </div>
      </div>

      <div class="favorites-page__grid">
        <div v-for="product in favorites" :key="product.id" class="favorites-page__item">
          <ProductCard :product="product" context="favorites" />
        </div>
      </div>

      <div class="favorites-page__recommended">
        <h3 class="favorites-page__recommended-title">Рекомендуемые товары</h3>
        <div class="favorites-page__grid">
          <div
            v-for="product in recommendedProducts"
            :key="product.id"
            class="favorites-page__item"
          >
            <ProductCard :product="product" />
          </div>
        </div>
      </div>
    </div>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useFavoritesWorkspace } from '@/modules/profile/public'
import { useAuthSession } from '@/modules/auth/public'
import { useCartActions } from '@/modules/order/public'
import { ProductCard, type Product } from '@/modules/catalog/public'
import UiButton from '@/shared/ui/UiButton.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'

const favoritesStore = useFavoritesWorkspace()
const authSession = useAuthSession()
const cartActions = useCartActions()
const router = useRouter()

const favorites = computed(() => favoritesStore.favorites.value)
const recommendedProducts = ref<Product[]>([])
const isSupplier = computed(() => authSession.isSupplier.value)

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

async function clearFavorites() {
  try {
    await favoritesStore.clearAll()
  } catch {
    // ignore
  }
}

async function createOrder() {
  if (!authSession.isAuthenticated.value) {
    router.push('/login?redirect=/checkout')
    return
  }
  // Добавляем все товары из избранного в корзину
  const itemsToAdd = favorites.value.filter((p) => p.inStock)
  for (const product of itemsToAdd) {
    try {
      await cartActions.addItem({
        productId: product.id,
        name: product.name,
        price: product.price,
        sku: product.sku,
        image: product.images?.[0] || product.imageUrl || '/placeholder-product.svg',
        quantity: 1,
      })
    } catch {
      // ignore individual errors
    }
  }
  router.push('/checkout')
}

onMounted(async () => {
  // initializeStore() в сторе мог отработать до восстановления сессии и решить,
  // что пользователь гость - перепроверяем здесь, на заходе на страницу.
  if (authSession.isAuthenticated.value) {
    try {
      await favoritesStore.loadFavorites()
    } catch {
      // ignore - покажем то, что уже есть в сторе
    }
  }

  try {
    const recs = await favoritesStore.fetchRecommended()
    if (Array.isArray(recs)) recommendedProducts.value = recs
  } catch {
    recommendedProducts.value = []
  }
})
</script>

<style scoped>
.favorites-page {
  padding-top: 24px;
  padding-bottom: 48px;
}

.favorites-page__header {
  text-align: center;
  margin-bottom: 32px;
}

.favorites-page__title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
}

.favorites-page__subtitle {
  margin: 0;
  color: #586578;
}

.favorites-page__empty {
  text-align: center;
  padding: 48px 16px;
}

.favorites-page__empty-icon {
  font-size: 64px;
  color: #cbd5e1;
  display: block;
  margin-bottom: 16px;
}

.favorites-page__empty-title {
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 600;
}

.favorites-page__empty-text {
  margin: 0 0 24px;
  color: #586578;
}

.favorites-page__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.favorites-page__count {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
}

.favorites-page__count-sub {
  margin: 0;
  font-size: 14px;
  color: #586578;
}

.favorites-page__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.favorites-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
}

.favorites-page__item {
  min-width: 0;
}

.favorites-page__recommended {
  margin-top: 48px;
}

.favorites-page__recommended-title {
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 600;
}
</style>
